import { useState, useEffect, useCallback } from "react";
import { authService } from "../services/authService";
import type { User } from "@/interfaces/User";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(() => {
    const currentUser = authService.getUser();
    if (currentUser && !currentUser.active) {
      authService.clearAuthData();
      return null;
    }
    return currentUser;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => authService.isAuthenticated());
  const [isLoading] = useState(false);

  // Configurar callbacks de sesión
  useEffect(() => {
    authService.setSessionCallbacks({
      onSessionExpiring: (timeLeft: number) => {
        console.log(
          `Sesión expirando en ${Math.round(timeLeft / 1000 / 60)} minutos`
        );
        // Aquí podrías mostrar una notificación o modal
      },
      onSessionExpired: () => {
        setUser(null);
        setIsAuthenticated(false);
      },
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      // Forzar logout local
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const validateSession = useCallback(async (): Promise<boolean> => {
    try {
      const isValid = await authService.validateSessionWithUser();
      if (!isValid) {
        await logout();
      }
      return isValid;
    } catch (error) {
      await logout();
      return false;
    }
  }, [logout]);

  const getToken = useCallback(() => {
    return authService.getToken();
  }, []);

  return {
    user,
    isAuthenticated,
    isLoading,
    logout,
    validateSession,
    getToken,
  };
};
